import { useState, useLayoutEffect, useRef } from 'preact/hooks';
import { MarkdownContent } from '../shared-ui/MarkdownContent';
import './HandoffDivider.css';

interface Props {
  content: string;
  /** ISO timestamp set once a later session injected this handoff (#1402 Phase A). */
  consumedAt?: string;
  /** 'full' (real summary) | 'fallback' (minimal stub) — mirrors the row kind. */
  status?: string;
}

export function HandoffDivider({ content, consumedAt, status }: Props) {
  const [expanded, setExpanded] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const didMount = useRef(false);

  // Toggling expand/collapse changes this element's height inside `.chat-area`,
  // which uses `contain: layout style` + momentum scrolling (style.css). On mobile
  // Chrome the sibling messages below relayout to their new positions but the
  // scroller is NOT re-rastered, so their stale paint overlaps the expanded
  // content until the next scroll. Force a zero-net scroll round-trip on the
  // scroller to trigger a re-raster — invisible (position unchanged), and skipped
  // on first mount so it never interferes with initial auto-scroll-to-bottom.
  // #1402 Phase C.
  useLayoutEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    const scroller = rootRef.current?.closest('.chat-area') as HTMLElement | null;
    if (!scroller) return;
    const id = requestAnimationFrame(() => {
      const top = scroller.scrollTop;
      // Nudge by 1px and back within the frame — forces the contained scroller to
      // re-raster its children without moving the visible scroll position.
      scroller.scrollTop = top + (top > 0 ? -1 : 1);
      void scroller.offsetHeight; // flush layout between the two writes
      scroller.scrollTop = top;
    });
    return () => cancelAnimationFrame(id);
  }, [expanded]);

  return (
    <div ref={rootRef} class={`handoff-divider${expanded ? ' expanded' : ''}`} onClick={() => setExpanded((v) => !v)}>
      <div class="handoff-divider-header">
        <span class="handoff-divider-icon">↻</span>
        <span class="handoff-divider-label">Session handoff · tap to {expanded ? 'collapse ▲' : 'expand ▼'}</span>
        {consumedAt && (
          <span
            class={`handoff-delivered${status === 'fallback' ? ' fallback' : ''}`}
            title={`Injected into the next session${status === 'fallback' ? ' as a fallback stub (/handoff produced no full summary)' : ''} at ${consumedAt}`}
          >
            ✓ delivered{status === 'fallback' ? ' (fallback)' : ''}
          </span>
        )}
      </div>
      {expanded && (
        <div class="handoff-divider-content" onClick={(e) => e.stopPropagation()}>
          <MarkdownContent text={content} />
        </div>
      )}
    </div>
  );
}
